import { FC, useRef } from "react";

import Stack from "@mui/material/Stack";
import { useFormContext } from "react-hook-form";

import { useRateContext } from "../../Context/RateContext";
import { useRateFieldName } from "../../hooks/useRateFieldName";
import { DescriptionField } from "../../RateFields/DescriptionField/DescriptionField";
import { MaxPerOrderField } from "../../RateFields/MaxPerOrderField/MaxPerOrderField";
import { MinPerOrderField } from "../../RateFields/MinPerOrderField/MinPerOrderField";
import { NameField } from "../../RateFields/NameField/NameField";
import { SeatsField } from "../../RateFields/SeatsField/SeatsField";
import { ExtrasSection } from "../../shared/ExtrasSection/ExtrasSection";
import { ExtrasSummary } from "../../shared/ExtrasSummary/ExtrasSummary";
import { RateCardShell } from "../../shared/RateCardShell/RateCardShell";
import { RateFieldsRow } from "../../shared/RateFieldsRow/RateFieldsRow";
import { CategoriesRow } from "../../shared/RateOptions/OptionRows/CategoriesRow";
import { MemberOnlyRow } from "../../shared/RateOptions/OptionRows/MemberOnlyRow";
import { PayWhatYouCanRow } from "../../shared/RateOptions/OptionRows/PayWhatYouCanRow";
import { ScheduleRow } from "../../shared/RateOptions/OptionRows/ScheduleRow/ScheduleRow";
import { TaxReceiptRow } from "../../shared/RateOptions/OptionRows/TaxReceiptRow/TaxReceiptRow";
import { RateOptionsGroup } from "../../shared/RateOptions/RateOptionsGroup";
import { RateStatusChip } from "../../shared/RateStatusChip/RateStatusChip";
import { LotteryEntryFields } from "./LotteryEntryFields";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { parseEntryCountFromTitle } from "@/common/helpers/entryCountInTitle";
import { useTranslate } from "@/common/hooks/useTranslate";
import { isSweepstakesBaseEntryRateRequired } from "@/features/LiveFormEditor/common/sweepstakes";
import { StepTicketsFormData } from "@/features/LiveFormEditor/Steps/StepTickets/types";

interface DrawingRateProps {
  totalCardNumber: number;
  shouldDisplayDeleteButton?: boolean;
  shouldConfirmDelete?: boolean;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  questionsArchivedOnDelete?: string[];
}

export const DrawingRate: FC<DrawingRateProps> = (props) => {
  const { t } = useTranslate();
  const { getValues, setValue } = useFormContext<StepTicketsFormData>();
  const { isSweepstakes } = useRateContext();
  const { organization } = useCurrentUserContext();
  const canPayWhatYouCan =
    !isSweepstakes || !isSweepstakesBaseEntryRateRequired(organization?.country, organization?.type);
  const isLegacyReceiptEnabled = Boolean(getValues(useRateFieldName("isGeneratingReceipt")));
  const entryCountFieldName = useRateFieldName("attendeesByTicket");

  const autoFilledEntryCountRef = useRef<number | null>(null);
  const fillEntryCountFromTitle = (title: string) => {
    const currentEntryCount = Number(getValues(entryCountFieldName));
    if (currentEntryCount !== 1 && currentEntryCount !== autoFilledEntryCountRef.current) {
      return;
    }
    const titleEntryCount = parseEntryCountFromTitle(title);
    const nextEntryCount = titleEntryCount && titleEntryCount > 1 ? titleEntryCount : 1;
    autoFilledEntryCountRef.current = nextEntryCount;
    if (nextEntryCount !== currentEntryCount) {
      setValue(entryCountFieldName, nextEntryCount, { shouldDirty: true, shouldValidate: true });
    }
  };

  return (
    <RateCardShell
      {...props}
      statusChip={<RateStatusChip />}
      fallbackName={isSweepstakes ? t("dashboard", "sweepstakes.rates.fallbackName") : undefined}
    >
      <Stack spacing={1.5}>
        <NameField onValueChange={fillEntryCountFromTitle} />
        <LotteryEntryFields />
        <DescriptionField />
        <ExtrasSection
          mode="toggle"
          variant="options-panel"
          optionsLabel={t("dashboard", "sweepstakes.rates.options")}
          collapsedSummary={<ExtrasSummary />}
        >
          <RateOptionsGroup
            title={t("dashboard", "ticketing.rateOptionsGroup.capacity")}
            beforeRows={
              <RateFieldsRow>
                {isSweepstakes ? (
                  <SeatsField tooltip={t("dashboard", "sweepstakes.rates.seatsTooltip")} />
                ) : (
                  <SeatsField />
                )}
                <MinPerOrderField />
                <MaxPerOrderField />
              </RateFieldsRow>
            }
          >
            <ScheduleRow isSweepstakes={isSweepstakes} />
            <MemberOnlyRow />
          </RateOptionsGroup>
          <RateOptionsGroup
            title={t(
              "dashboard",
              isSweepstakes ? "sweepstakes.rates.entryType" : "ticketing.rateOptionsGroup.ticketType"
            )}
          >
            {canPayWhatYouCan && <PayWhatYouCanRow />}
            <CategoriesRow />
          </RateOptionsGroup>
          {isLegacyReceiptEnabled && (
            <RateOptionsGroup title={t("dashboard", "ticketing.rateOptionsGroup.taxesAndReceipts")}>
              <TaxReceiptRow
                tooltip={isSweepstakes ? t("dashboard", "sweepstakes.rates.taxReceiptInformation") : undefined}
              />
            </RateOptionsGroup>
          )}
        </ExtrasSection>
      </Stack>
    </RateCardShell>
  );
};
